import { FiImage, FiLoader, FiArrowRight, FiLayers, FiVideo } from 'react-icons/fi';
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import SafeIcon from '../../common/SafeIcon';


const Thumbnail = ({ src, type }) => {
  const [hasError, setHasError] = useState(false);

  if (!src || hasError) {
    return <SafeIcon icon={type === 'video' ? FiVideo : FiImage} className="text-charcoal-400 text-xs" />;
  }

  return (
    <img
      src={src}
      alt=""
      className="w-full h-full object-cover"
      onError={() => setHasError(true)}
    />
  );
};

const SearchResults = ({ results, isLoading, onClose, query }) => {
  if (isLoading) {
    return (
      <div className="mt-2 w-full bg-white dark:bg-charcoal-900 p-4 rounded-lg">
        <div className="flex items-center justify-center space-x-2 text-charcoal-500">
          <SafeIcon icon={FiLoader} className="animate-spin" />
          <span>Searching...</span>
        </div>
      </div>
    );
  }

  const hasCampaigns = results?.campaigns?.length > 0;
  const hasCreatives = results?.creatives?.length > 0;
  const hasNav = results?.nav?.length > 0;

  if (!results || (!hasCampaigns && !hasCreatives && !hasNav)) {
    if (!query) return null;
    return (
      <div className="mt-2 w-full bg-white dark:bg-charcoal-900 p-4 rounded-lg">
        <p className="text-charcoal-500 text-center">No results found for "{query}"</p>
      </div>
    );
  }

  return (
    <div className="mt-2 w-full bg-white dark:bg-charcoal-900 border-t border-charcoal-100 dark:border-charcoal-800 animate-fade-in max-h-[60vh] overflow-y-auto rounded-b-lg">

      {/* Navigation Section */}
      {hasNav && (
        <div className="py-2">
          <div className="px-4 py-2 text-xs font-semibold text-charcoal-400 uppercase tracking-wider flex items-center space-x-2">
            <SafeIcon icon={FiArrowRight} />
            <span>Pages & Tools</span>
          </div>
          <ul>
            {results.nav.map((item) => (
              <li key={item.path}>
                <Link
                  to={item.path}
                  onClick={onClose}
                  className="block px-4 py-2 hover:bg-charcoal-50 dark:hover:bg-charcoal-800 transition-colors"
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded flex items-center justify-center">
                        <SafeIcon icon={item.icon} />
                    </div>
                    <span className="text-sm font-medium text-charcoal-900 dark:text-charcoal-100">{item.name}</span>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}

      {(hasNav && (hasCampaigns || hasCreatives)) && <div className="border-t border-charcoal-100 dark:border-charcoal-800"></div>}

      {/* Campaigns Section */}
      {hasCampaigns && (
        <div className="py-2">
          <div className="px-4 py-2 text-xs font-semibold text-charcoal-400 uppercase tracking-wider flex items-center space-x-2">
            <SafeIcon icon={FiLayers} />
            <span>Campaigns</span>
          </div>
          <ul>
            {results.campaigns.map((campaign) => (
              <li key={campaign.id}>
                <Link
                  to="/campaigns"
                  onClick={onClose}
                  className="block px-4 py-3 hover:bg-charcoal-50 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-charcoal-900 truncate max-w-[250px]">{campaign.name}</p>
                      <div className="flex items-center space-x-2 mt-1">
                        <span className={`text-[10px] px-2 py-0.5 rounded-full ${
                          campaign.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-charcoal-100 text-charcoal-600'
                        }`}>
                          {campaign.status}
                        </span>
                        <span className="text-xs text-charcoal-500">{campaign.platforms?.length || 0} Platforms</span>
                      </div>
                    </div>
                    <SafeIcon icon={FiArrowRight} className="text-charcoal-300" />
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Divider */}
      {hasCampaigns && hasCreatives && (
        <div className="border-t border-charcoal-100 dark:border-charcoal-800"></div>
      )}

      {/* Creatives Section */}
      {hasCreatives && (
        <div className="py-2">
          <div className="px-4 py-2 text-xs font-semibold text-charcoal-400 uppercase tracking-wider flex items-center space-x-2">
            <SafeIcon icon={FiImage} />
            <span>Creatives</span>
          </div>
          <ul>
            {results.creatives.map((creative) => (
              <li key={creative.id}>
                <Link
                  to="/engagement?tab=library"
                  onClick={onClose}
                  className="block px-4 py-3 hover:bg-charcoal-50 dark:hover:bg-charcoal-800 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-charcoal-100 dark:bg-charcoal-800 rounded flex items-center justify-center overflow-hidden flex-shrink-0">
                        <Thumbnail src={creative.image_url} type={creative.type} />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-charcoal-900 dark:text-charcoal-100 truncate max-w-[200px]">{creative.name}</p>
                        <p className="text-xs text-charcoal-500 dark:text-charcoal-400 capitalize">{creative.type}</p>
                      </div>
                    </div>
                    <SafeIcon icon={FiArrowRight} className="text-charcoal-300 dark:text-charcoal-600" />
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default SearchResults;
